/**
 * Result classification utilities.
 *
 * Helper functions to classify and check form submission results.
 * All functions are SSR-safe pure functions.
 *
 * @module utils/classify-result
 */

import type { FormSubmissionResult } from "../types/care-seeker";
import type { FormResponse } from "../types/form-response";

/**
 * Classify a form response into a submission result type.
 *
 * This is useful for analytics tracking and conditional UI rendering
 * based on the submission outcome.
 *
 * @param response - The form submission response
 * @returns The classification of the result
 *
 * @example
 * ```ts
 * const response = await submitForm(data);
 * const result = classifyFormResult(response);
 *
 * if (result === "success") {
 *   trackSuccess();
 * } else if (result === "duplicate") {
 *   showDuplicateMessage();
 * }
 * ```
 */
export function classifyFormResult<T>(
  response: FormResponse<T>,
): FormSubmissionResult {
  if (response.success) {
    return "success";
  }

  switch (response.error.code) {
    case "duplicate":
      return "duplicate";
    case "validation":
      return "validation-error";
    case "server":
      return "server-error";
    default:
      return "server-error";
  }
}

/**
 * Check if a response is a duplicate error.
 *
 * @param response - The form submission response
 * @returns True if the response is a duplicate error
 *
 * @example
 * ```ts
 * if (isDuplicateError(response)) {
 *   console.log("Lead already exists");
 * }
 * ```
 */
export function isDuplicateError<T>(response: FormResponse<T>): boolean {
  return !response.success && response.error.code === "duplicate";
}

/**
 * Check if a response is a server error.
 *
 * @param response - The form submission response
 * @returns True if the response is a server error
 *
 * @example
 * ```ts
 * if (isServerError(response)) {
 *   console.error("Internal server error occurred");
 * }
 * ```
 */
export function isServerError<T>(response: FormResponse<T>): boolean {
  return !response.success && response.error.code === "server";
}

/**
 * Check if a response is a validation error.
 *
 * @param response - The form submission response
 * @returns True if the response is a validation error
 *
 * @example
 * ```ts
 * if (isValidationError(response)) {
 *   console.log("Validation failed:", response.error.errors);
 * }
 * ```
 */
export function isValidationError<T>(response: FormResponse<T>): boolean {
  return !response.success && response.error.code === "validation";
}
